import { Participant } from '../../../types/generated/nikeone';

export declare type TurboLiveEntity =
    | Bet
    | LiveMatch
    | OverviewMarket
    | DetailMarket
    | Sport
    | Tournament
    | MarketCategory
    | Mbm;

export interface ChannelStatus {
    channelId: number;
    status: ChannelState;
}

export enum ChannelState {
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
    DELAYED = 'DELAYED'
}

export interface MatchIdentity {
    id: string;
    tournamentId: string;
    sportId: string;
    radar: boolean;
    video: boolean;
}

export interface Sport {
    id: string;
    sportName: LocalizedText;
    slug: string;
    layout: string;
    order: number;
    maxColumns: number;
    overviewColumns: {
        marketId: string;
        switchAllowed: boolean;
    }[];
}

export interface Tournament {
    id: string;
    sportId: string;
    countryId: string;
    statisticsId?: string | null;
    order: number;
    sportName: LocalizedText;
    tournamentName: LocalizedText;
    note?: LocalizedText;
    tournamentAllowMarketCombinations: boolean;
}

export interface LiveMatch {
    id: string;
    sportId: string;
    countryId: string;
    tournamentId: string;
    startTime: string;
    statisticsId?: string;
    streamId?: string;
    radarId?: string;
    analysisId?: string;
    home: LocalizedText;
    away: LocalizedText;
    note?: LocalizedText;
    betCount: number;
    timer: MatchTimer;
    extras: MatchExtras;
    score: MatchScore;
    stats: { [key in StatisticsName]: string };
}

export interface Prematch {
    sportEventId: string;
    sportId: string;
    countryId: string;
    expiration: string;
    tournamentId: string;
    statisticsId?: string;
    matchTemplate?: string;
    streamId?: string;
    radarId?: string;
    analysisId?: string;
    participants: [];
    betCount: number;
}

export enum MatchStatus {
    SCHEDULED = 'SCHEDULED',
    IN_PROGRESS = 'IN_PROGRESS',
    INTERRUPTED = 'INTERRUPTED',
    FINISHED = 'FINISHED',
    BREAK = 'BREAK',
    CANCELLED = 'CANCELLED'
}

export enum TimerFormat {
    MM_SS = 'mm:ss',
    MM = "'mm"
}

export interface MatchTimer {
    clockStopped: boolean;
    countDown: boolean;
    currentPeriod: LocalizedText;
    eventStatus: MatchStatus;
    timestamp?: number;
    matchSeconds?: number;
}

export enum ScoreboardPeriodType {
    PERIOD = 'PERIOD',
    HALFTIME = 'HALFTIME',
    QUARTER = 'QUARTER',
    THIRD = 'THIRD',
    OVERTIME = 'OVERTIME',
    PENALTIES = 'PENALTIES',
    SET = 'SET',
    LEG = 'LEG',
    INNING = 'INNING',
    MAP = 'MAP'
}

export interface ScoreboardType {
    id: string;
    showTimer?: boolean;
    showRedCards?: boolean;
    showYellowCards?: boolean;
    showCorners?: boolean;
    timerFormat?: TimerFormat;
    periods: ScoreboardPeriod[];
}

export interface ScoreboardPeriod {
    type: ScoreboardPeriodType;
    order: number;
    header: LocalizedText;
    tooltip: LocalizedText;
}

export enum MatchScoreServer {
    HOME = 'HOME',
    AWAY = 'AWAY',
    NONE = 'NONE'
}

export interface ScoreValue {
    home: string;
    away: string;
}

export interface PeriodScore {
    order: number;
    score: ScoreValue;
}

export enum StatisticsName {
    UNKNOWN = 'UNKNOWN',
    CORNERS = 'CORNERS',
    RED_CARDS = 'RED_CARDS',
    YELLOW_CARDS = 'YELLOW_CARDS',
    YELLOWRED_CARDS = 'YELLOWRED_CARDS',
    SUSPENDS = 'SUSPENDS'
}

export enum MatchScoreType {
    TOTAL = 'TOTAL',
    PERIOD = 'PERIOD',
    GAME = 'GAME',
    PENALTIES = 'PENALTIES'
}

export interface MatchScore {
    server: MatchScoreServer;
    scores: {
        [key in MatchScoreType]: ScoreValue;
    };
    periodScores: PeriodScore[];
}

export interface MatchExtraEntry {
    name: string;
    parameters: string[];
    header?: LocalizedText;
    tooltip?: LocalizedText;
}

export interface MatchExtras {
    [name: string]: MatchExtraEntry;
}

export interface Bet {
    id: string;
    sportId: string;
    countryId: string;
    tournamentId: string;
    matchId: string;
    marketId: string;
    order: number;
    channelId: number;
    state: BetState;
    selections: Selection[];
    header?: LocalizedText;
    betName?: LocalizedText;
    betParam?: string;
    betNumber: number;
    participants?: LocalizedText[];
    overview: {
        label?: LocalizedText;
        additionalLabel?: LocalizedText;
    };
    detail: {
        label?: LocalizedText;
        additionalLabel?: LocalizedText;
    };
    betParticipants: Participant[];
}

export enum BetState {
    Running = 'Running',
    Stopped = 'Stopped',
    Hidden = 'Hidden',
    Resulted = 'Resulted'
}

export interface Selection {
    name: LocalizedText;
    code: number;
    odds: number;
    enabled: boolean;
    locked: boolean;
}

export interface OverviewMarket {
    id: string;
    name: LocalizedText;
    sportId: string;
    order: number;
    layout: MarketLayout;
    columns: number;
    headers: { header: LocalizedText; headerInfoText: LocalizedText }[];
    info?: LocalizedText;
}

export enum MarketParticipantCheck {
    none = 'none',
    direct = 'direct',
    with_team = 'with_team',
    team_once = 'team_once'
}

export interface DetailMarket {
    id: string;
    name: LocalizedText;
    sportId: string;
    collapse: string;
    order: number;
    layout: MarketLayout;
    showStatistics?: boolean;
    info?: LocalizedText;
    groupIdInstancia?: number;
    marketCombinable: boolean;
    marketUniqueParticipantCheck: MarketParticipantCheck;
    marketGroupId: number;
    marketGroupCombinationMask: number;
}

export interface MarketLayout {
    id: string;
    rows: number;
    cols: number;
    cells: MarketLayoutCell[][];
    narrowCol?: number;
    showAdditionalLabel: boolean;
}

export interface MarketLayoutCell {
    type: MarketLayoutCellType;
    row: number;
    col: number;
    betParticipantIndex?: number;
    selectionIndex?: number;
    betParamIndex?: number;
    bold?: boolean;
}

export enum MarketLayoutCellType {
    betName = 'betName',
    selection = 'selection',
    betParticipant = 'betParticipant',
    matchName = 'matchName',
    label = 'label',
    betParam = 'betParam',
    empty = 'empty'
}

export interface LocalizedText {
    en?: string;
    hu?: string;
    sk?: string;
}

export interface MarketCategory {
    id: string;
    sportId: string;
    name: LocalizedText;
    order: number;
    marketIds: string[];
}

export enum MBMType {
    BEST_OF_MAPS = 'BEST_OF_MAPS',
    BEST_OF_ROUNDS_IN_OT = 'BEST_OF_ROUNDS_IN_OT',
    CORNER = 'CORNER',
    END_OF_PERIOD = 'END_OF_PERIOD',
    FIRST_AEGIS = 'FIRST_AEGIS',
    FIRST_BARON = 'FIRST_BARON',
    FIRST_BARRACKS = 'FIRST_BARRACKS',
    FIRST_BLOOD = 'FIRST_BLOOD',
    FIRST_DRAGON = 'FIRST_DRAGON',
    FIRST_INHIBITOR = 'FIRST_INHIBITOR',
    FIRST_TOWER = 'FIRST_TOWER',
    GAME_SCORE = 'GAME_SCORE',
    LEG_SCORE = 'LEG_SCORE',
    PERIOD_SCORE = 'PERIOD_SCORE',
    POINT_SCORE = 'POINT_SCORE',
    RAIN_DELAY = 'RAIN_DELAY',
    ROUNDS_PER_MAP = 'ROUNDS_PER_MAP',
    SCORE = 'SCORE',
    SET_SCORE = 'SET_SCORE',
    TENIS_SCORE_GAME = 'TENIS_SCORE_GAME',
    TENIS_SCORE_POINT = 'TENIS_SCORE_POINT',
    REDCARD = 'REDCARD',
    YELLOWCARD = 'YELLOWCARD'
}

export enum MBMParticipant {
    HOME = 'HOME',
    AWAY = 'AWAY'
}

export enum MBMTieBreak {
    TIEBREAK = 'TIEBREAK',
    SUPERTIEBREAK = 'SUPERTIEBREAK'
}

export interface Mbm {
    id: string;
    period: LocalizedText;
    order: number;
    timeSeconds: number;
    game: LocalizedText;
    type: MBMType;
    score: ScoreValue;
    participant: MBMParticipant;
    player: string;
    info: LocalizedText;
    point: LocalizedText;
    corner: LocalizedText;
    tieBreak: MBMTieBreak;
    periodOrder: number;
}
